import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Animated,
  Easing,
  Dimensions,
  Linking,
  DeviceEventEmitter,
  Platform,
  Pressable,
  Text,
  View,
  ActivityIndicator,
} from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { WebView } from "~/ui/webview"
import { observer } from "mobx-react-lite"
import { useStores } from "~models"
import useMediaLibrary from "~hooks/useMediaLibrary"
import { alertError } from "~utils/alert"
import { WEB_URL } from "~config/env"
import { appFixedViewportStyle } from "~ui/app-shell"

export const WEBVIEW_CLOSED_EVENT = "onWebviewClosed"

const WebviewContext = createContext<{
  webviewRef: React.RefObject<WebView>
  visible: boolean
  url: string
  webviewCloseSeq: number
  preloadWebview: (webUrl: string) => void
  showWebview: () => void
  hideWebview: () => void
  sendMessage: (data: unknown) => void
  openWebview: (options: OpenWebviewOptions) => void
  clearWebview: () => void
} | null>(null)
const { width } = Dimensions.get("window")
/** 覆盖钱包页左上角返回按钮的点击热区 */
const WEBVIEW_BACK_OVERLAY_WIDTH = 56
const WEBVIEW_BACK_OVERLAY_HEIGHT = 44

export type OpenWebviewOptions = {
  path: string
  state?: Record<string, unknown>
}

function normalizeWebPath(path: string) {
  return path.startsWith("/") ? path : `/${path}`
}

function formatBearerToken(token: string) {
  const raw = token.trim()
  return raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`
}

/** 钱包 H5 必须在根路径带 token 启动，才能完成登录态初始化 */
export function buildWebviewBaseUrl(token: string) {
  const base = WEB_URL.replace(/\/$/, "")
  const authToken = formatBearerToken(token)
  return `${base}?token=${encodeURIComponent(authToken)}`
}

/** 入口 URL：token 在 query，目标路由在 hash（与 postMessage 双保险） */
export function buildWebviewEntryUrl(
  path: string,
  token: string,
  state?: Record<string, unknown>,
) {
  const base = buildWebviewBaseUrl(token)
  const normalized = normalizeWebPath(path)
  if (!state || Object.keys(state).length === 0) {
    return `${base}#${normalized}`
  }
  const qs = new URLSearchParams()
  Object.entries(state).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      qs.set(key, String(value))
    }
  })
  const query = qs.toString()
  return query ? `${base}#${normalized}?${query}` : `${base}#${normalized}`
}

function buildWebviewNavigateMessage(path: string, state?: Record<string, unknown>) {
  const normalized = normalizeWebPath(path)
  return state
    ? JSON.stringify({ type: "OPEN_WEB_STATE", path: normalized, state })
    : JSON.stringify({ type: "OPEN_WEB", path: normalized })
}

type WebviewLayerProps = {
  url: string
  onBack: () => void
  onMessage: (event: { nativeEvent: { data: string } }) => void
  webviewRef: React.RefObject<WebView>
  webIframeRef: React.MutableRefObject<HTMLIFrameElement | null>
  visible: boolean
  loading: boolean
  webviewKey: number
  onIframeLoad: () => void
}

function WebviewLoadingOverlay({ text = "加载中..." }: { text?: string }) {
  return (
    <View
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        backgroundColor: "#fff",
      }}
    >
      <ActivityIndicator size="large" color="#07c160" />
      <Text style={{ marginTop: 12, fontSize: 14, color: "#666" }}>{text}</Text>
    </View>
  )
}

function WebviewBackOverlay({ top, left, onBack }: { top: number; left: number; onBack: () => void }) {
  return (
    <Pressable
      onClick={onBack}
      aria-label="返回"
      style={{
        position: "absolute",
        top,
        left,
        zIndex: 2,
        width: WEBVIEW_BACK_OVERLAY_WIDTH,
        height: WEBVIEW_BACK_OVERLAY_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        padding: 0,
      }}
    >
      <Text style={{ fontSize: 28, lineHeight: 1, color: "transparent", userSelect: "none" }}>‹</Text>
    </Pressable>
  )
}

function WebviewLayer({
  url,
  onBack,
  onMessage,
  webviewRef,
  webIframeRef,
  visible,
  loading,
  webviewKey,
  onIframeLoad,
}: WebviewLayerProps) {
  const insets = useSafeAreaInsets()
  const translateX = useRef(new Animated.Value(width)).current

  useEffect(() => {
    if (Platform.OS === "web") return
    Animated.timing(translateX, {
      toValue: visible ? 0 : width,
      duration: 0,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start()
  }, [visible, translateX])

  useEffect(() => {
    if (Platform.OS !== "web" || !url || typeof window === "undefined") return
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== webIframeRef.current?.contentWindow) return
      if (typeof event.data !== "string") return
      onMessage({ nativeEvent: { data: event.data } })
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [url, onMessage, webIframeRef])

  if (!url) return null

  if (Platform.OS === "web") {
    if (typeof document === "undefined") return null
    return createPortal(
      <div
        style={{
          ...appFixedViewportStyle,
          zIndex: visible ? 10000 : -1,
          visibility: visible ? "visible" : "hidden",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
          backgroundColor: "#fff",
        }}
      >
        <iframe
          key={webviewKey}
          ref={webIframeRef}
          src={url}
          onLoad={onIframeLoad}
          title="wallet"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            backgroundColor: "#fff",
          }}
        />
        {visible && loading ? <WebviewLoadingOverlay /> : null}
        {visible ? <WebviewBackOverlay top={insets.top} left={0} onBack={onBack} /> : null}
      </div>,
      document.body,
    )
  }

  return (
    <Animated.View
      style={{
        position: "absolute",
        top: insets.top,
        left: 0,
        width,
        bottom: insets.bottom - 10,
        zIndex: 9999,
        backgroundColor: "white",
        transform: [{ translateX }],
      }}
      pointerEvents={visible ? "auto" : "none"}
    >
      <View style={{ flex: 1, position: "relative" }}>
        <WebView
          key={webviewKey}
          ref={webviewRef}
          source={{ uri: url }}
          onMessage={onMessage}
          onLoadEnd={onIframeLoad}
          style={{ flex: 1 }}
          injectedJavaScript={`screen.orientation.lock('portrait');`}
        />
        {visible && loading ? <WebviewLoadingOverlay /> : null}
        <WebviewBackOverlay top={0} left={0} onBack={onBack} />
      </View>
    </Animated.View>
  )
}

type WebviewHistoryState = {
  chatredWebview?: boolean
  webviewPath?: string
}

const WebviewProviderComponent = ({ children }) => {
  const { authStore, eventStore, walletStore, websocketStore } = useStores()
  const navigate = useNavigate()
  const location = useLocation()
  const webviewRef = useRef(null)
  const webIframeRef = useRef<HTMLIFrameElement | null>(null)
  const pendingMessagesRef = useRef<string[]>([])
  const pendingNavigatePayloadRef = useRef<string | null>(null)
  const webviewHistoryPushedRef = useRef(false)
  const iframeReadyRef = useRef(false)
  const loadedTokenRef = useRef("")
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const visibleRef = useRef(false)
  const [url, setUrl] = useState("")
  const entryPathRef = useRef("")
  const [webviewKey, setWebviewKey] = useState(0)
  const [webviewCloseSeq, setWebviewCloseSeq] = useState(0)
  const { saveAssetAsync } = useMediaLibrary({ writeOnly: false, requestPermission: false })
  const preloadWebview = useCallback((webUrl: string) => {
    setUrl(webUrl)
  }, [])
  const showWebview = () => setVisible(true)

  const resumeChatWebsocket = useCallback(() => {
    if (authStore.hasToken) {
      websocketStore.reconnect()
    }
  }, [authStore.hasToken, websocketStore])

  const refreshUserInfoAfterWebview = useCallback(() => {
    if (!authStore.hasToken) return
    void walletStore.getWalletInfo()
    void walletStore.getMessageDestroySetting()
    DeviceEventEmitter.emit("onBindAccountInfo")
    DeviceEventEmitter.emit(WEBVIEW_CLOSED_EVENT)
    setWebviewCloseSeq((seq) => seq + 1)
  }, [authStore.hasToken, walletStore])

  const hideWebview = useCallback(() => {
    const wasVisible = visibleRef.current
    const shouldPopHistory = webviewHistoryPushedRef.current
    visibleRef.current = false
    setVisible(false)
    setLoading(false)
    entryPathRef.current = ""
    if (wasVisible) {
      resumeChatWebsocket()
      refreshUserInfoAfterWebview()
    }
    if (Platform.OS === "web" && shouldPopHistory) {
      webviewHistoryPushedRef.current = false
      navigate(-1)
    }
  }, [navigate, refreshUserInfoAfterWebview, resumeChatWebsocket])

  useEffect(() => {
    visibleRef.current = visible
  }, [visible])

  const clearWebview = useCallback(() => {
    hideWebview()
    setUrl("")
    setLoading(false)
    pendingMessagesRef.current = []
    pendingNavigatePayloadRef.current = null
    iframeReadyRef.current = false
    loadedTokenRef.current = ""
    setWebviewKey((k) => k + 1)
  }, [hideWebview])

  const flushPendingMessages = useCallback(() => {
    const payload = pendingNavigatePayloadRef.current
    if (!payload) return

    const deliver = () => {
      if (Platform.OS === "web") {
        const target = webIframeRef.current?.contentWindow
        if (!target) return
        target.postMessage(payload, "*")
        return
      }
      webviewRef.current?.postMessage(payload)
    }

    deliver()
    setTimeout(deliver, 300)
    setTimeout(() => {
      deliver()
      pendingNavigatePayloadRef.current = null
      pendingMessagesRef.current = []
    }, 900)
  }, [])

  const finishWarmNavigation = useCallback(() => {
    flushPendingMessages()
    window.setTimeout(() => setLoading(false), 400)
  }, [flushPendingMessages])

  const handleIframeLoad = useCallback(() => {
    iframeReadyRef.current = true
    flushPendingMessages()
    setLoading(false)
  }, [flushPendingMessages])

  const isWebviewWarm = useCallback(() => {
    if (!iframeReadyRef.current || !url) return false
    if (Platform.OS === "web") {
      return Boolean(webIframeRef.current?.contentWindow)
    }
    return Boolean(webviewRef.current)
  }, [url])

  const saveWebImage = useCallback((url) => {
    try {
      saveAssetAsync({ uri: url })
    } catch (e) {
      alertError(e)
    }
  }, [])

  const postWebviewPayload = useCallback((payload: string) => {
    if (Platform.OS === "web") {
      const target = webIframeRef.current?.contentWindow
      if (target) {
        target.postMessage(payload, "*")
      } else {
        pendingMessagesRef.current.push(payload)
      }
      return
    }
    if (webviewRef.current) {
      webviewRef.current.postMessage(payload)
    }
  }, [])

  const sendMessage = (data) => {
    postWebviewPayload(JSON.stringify(data))
  }

  const handleWebviewBack = useCallback(() => {
    postWebviewPayload(JSON.stringify({ type: "BACK" }))
    postWebviewPayload(JSON.stringify({ type: "WEB_BACK" }))
    hideWebview()
  }, [hideWebview, postWebviewPayload])

  const openWebview = useCallback(
    ({ path, state }: OpenWebviewOptions) => {
      if (!authStore.hasToken) return
      const normalized = normalizeWebPath(path)
      const webSrc = buildWebviewEntryUrl(normalized, authStore.token, state)
      const payload = buildWebviewNavigateMessage(normalized, state)
      const warm = isWebviewWarm()

      entryPathRef.current = normalized
      pendingNavigatePayloadRef.current = payload
      pendingMessagesRef.current = [payload]
      websocketStore.close()
      setLoading(true)
      const openingFresh = !visibleRef.current
      visibleRef.current = true
      setVisible(true)

      if (!warm) {
        setUrl(webSrc)
        setWebviewKey((k) => k + 1)
      }

      if (Platform.OS === "web" && openingFresh) {
        navigate(
          { pathname: location.pathname, search: location.search },
          { state: { chatredWebview: true, webviewPath: normalized } satisfies WebviewHistoryState },
        )
        webviewHistoryPushedRef.current = true
        if (warm) {
          requestAnimationFrame(() => finishWarmNavigation())
        }
      } else if (warm) {
        requestAnimationFrame(() => finishWarmNavigation())
      } else {
        requestAnimationFrame(() => postWebviewPayload(payload))
      }
    },
    [
      authStore.hasToken,
      authStore.token,
      finishWarmNavigation,
      isWebviewWarm,
      location.pathname,
      location.search,
      navigate,
      postWebviewPayload,
      websocketStore,
    ],
  )

  useEffect(() => {
    if (!loading) return undefined
    const timer = window.setTimeout(() => setLoading(false), 15000)
    return () => window.clearTimeout(timer)
  }, [loading])

  useEffect(() => {
    if (Platform.OS !== "web") return
    if (!visibleRef.current) return
    const state = location.state as WebviewHistoryState | null
    if (state?.chatredWebview) return
    if (!webviewHistoryPushedRef.current) return
    webviewHistoryPushedRef.current = false
    visibleRef.current = false
    setVisible(false)
    setLoading(false)
    entryPathRef.current = ""
    resumeChatWebsocket()
    refreshUserInfoAfterWebview()
  }, [location.key, location.state, refreshUserInfoAfterWebview, resumeChatWebsocket])

  const onMessage = (event) => {
    try {
      const data = JSON.parse(event.nativeEvent.data)
      if (data.type === "BACK_APP") {
        hideWebview()
      } else if (data.type === "savePhotoByBase64") {
        saveWebImage(data.data.base64)
      } else if (["pay", "open"].includes(data.type)) {
        Linking.openURL(data.data)
      } else if (data.type === "LOGIN_REGISTER") {
        clearWebview()
        eventStore.publicLogout()
      } else if (data.type === "REFRESH_USER_INFO") {
        refreshUserInfoAfterWebview()
      } else if (data.type === "BACK_APP_WITH_INFO") {
        hideWebview()
      }
    } catch {
      //
    }
  }

  useEffect(() => {
    if (authStore.hasToken) {
      const baseUrl = buildWebviewBaseUrl(authStore.token)
      if (loadedTokenRef.current !== authStore.token) {
        loadedTokenRef.current = authStore.token
        iframeReadyRef.current = false
        setWebviewKey((k) => k + 1)
      }
      preloadWebview(baseUrl)
    } else {
      clearWebview()
    }
  }, [authStore.hasToken, authStore.token, clearWebview, preloadWebview])

  return (
    <WebviewContext.Provider
      value={{
        webviewRef,
        visible,
        url,
        webviewCloseSeq,
        preloadWebview,
        showWebview,
        hideWebview,
        sendMessage,
        openWebview,
        clearWebview,
      }}
    >
      {children}
      <WebviewLayer
        url={url}
        onBack={handleWebviewBack}
        onMessage={onMessage}
        webviewRef={webviewRef}
        webIframeRef={webIframeRef}
        visible={visible}
        loading={loading}
        webviewKey={webviewKey}
        onIframeLoad={handleIframeLoad}
      />
    </WebviewContext.Provider>
  )
}

export const WebviewProvider = observer(WebviewProviderComponent)

export const useWebview = () => {
  const ctx = useContext(WebviewContext)
  if (!ctx) {
    return {
      showWebview: () => {},
      hideWebview: () => {},
      sendMessage: () => {},
      openWebview: () => {},
      clearWebview: () => {},
      preloadWebview: () => {},
      visible: false,
      url: "",
      webviewCloseSeq: 0,
      webviewRef: { current: null },
    }
  }
  return ctx
}
